import { applyMiddleware, combineReducers, createStore, Reducer, Store } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunkMiddleware from 'redux-thunk';
import businessRules, { businessRulesInitialState, BusinessRulesState } from 'store/business-rules/reducer';
import cargos, { cargosInitialState, CargosState } from 'store/cargo/reducer';
import codelists, { codelistsInitialState, CodelistsState } from 'store/codelists/reducer';
import { DeclarationFooterState } from 'store/declaration-footer/declaration-footer';
import declarationFooter, { declarationFooterInitialState } from 'store/declaration-footer/reducer';
import { ParsedForm } from 'store/declaration-form-errors/ParsedForm';
import commodityCodeTaxes, {
    commodityCodeTaxesInitialState,
    CommodityCodeTaxesState,
} from 'store/declaration-product-taxes/reducer';
import declarationProduct, {
    DeclarationProductState,
    initialDeclarationProductState,
} from 'store/declaration-product/reducer';
import { DeclarationValidation } from 'store/declaration-validation/declaration-validation';
import declarationValidation, {
    initialState as decalarationValidationInitialState,
} from 'store/declaration-validation/reducer';
import depositRefunds, {
    initialRefundsDepositState,
    RefundsDepositState,
} from 'store/declarations/ireland/refunds/deposits/reducer';
import refunds, { initialRefundsState, RefundsState } from 'store/declarations/ireland/refunds/remission/reducer';
import digitalCertificates, {
    DigitalCertificateState,
    initialDigitalCertificateState,
} from 'store/digital-certificates/reducer';
import documents, { DocumentsState, initalDocumentsState } from 'store/documents/reducer';
import exchanges, { exchangesInitialState, ExchangesState } from 'store/exchanges/reducer';
import individuals, { individualsInitialState, IndividualsState } from 'store/individuals/reducer';
import jobs, { jobsInitialState, JobsState } from 'store/jobs/reducer';
import legs, { legsInitialState, LegsState } from 'store/journeys/legs/reducer';
import journeys, { journeysInitialState, JourneysState } from 'store/journeys/reducer';
import routes, { routesInitialState, RoutesState } from 'store/journeys/routes/reducer';
import products, { productsInitialState, ProductsState } from 'store/products/reducer';
import taricCodes, { taricCodeInitialState, TaricCodeState } from 'store/taric-codes/reducer';
import tooltips, { tooltipsInitialState, TooltipsState } from 'store/tooltips/reducer';
import vehicles, { vehiclesInitialState, VehiclesState } from 'store/vehicles/reducer';
import { NotificationProps } from '../components/ui/base/notification/Notification';
import loggerMiddleware from '../middleware/logger';
import { BreadcrumbItem } from '../store/breadcrumb/breadcrumbItem';
import breadcrumb from '../store/breadcrumb/reducer';
import countries, { countriesInitialState, CountriesState } from '../store/countries/reducer';
import currencies, { currenciesInitialState, CurrenciesState } from '../store/currencies/reducer';
import customers, { customersInitialState, CustomersState } from '../store/customers/reducer';
import declarationFormErrors, {
    initialState as declarationFormErrorsInitState,
} from '../store/declaration-form-errors/reducer';
import declarations, { DeclarationsState, initialDeclarationsState } from '../store/declarations/reducer';
import drivers, { driversInitialState, DriversState } from '../store/drivers/reducer';
import notification, { initialState as notificationInitialState } from '../store/notification/reducer';
import session, { sessionInitialState, SessionState } from '../store/session/reducers';
import weighbridges, { weighbridgesInitialState, WeighbridgesState } from 'store/weighbridges/reducer';
import globalOverlay, { globalOverlayInitialState } from 'store/global-overlay/reducer';
import { GlobalOverlayState } from 'store/global-overlay/globalOverlay';
import { ProductTemplateFormErrors } from '../store/product-template-form-errors/ProductTemplateFormErrors';
import productTemplateFormErrors, {
    initialState as productTemplateFormErrorsInitState,
} from '../store/product-template-form-errors/reducer';
import { DeclarationFilter } from '../store/filter/DeclarationFilter';
import declarationFilter, { initialState as declarationFilterInitState } from '../store/filter/reducer';
import { GvmsReferenceDataStore } from '../store/declarations/uk/gvms-declaration';
import gvmsReferenceDataReducer, { gvmsReferenceDataInitialState } from '../store/gvms-reference-data/reducer';
import manifests, { manifestsInitialState, ManifestsState } from 'store/manifests/reducer';
import teams, { initialTeamsState, TeamsState } from 'store/teams/reducer';
import invoicesReducer, { invoicesInitialState, InvoicesState } from 'store/invoices/reducer';
import invoiceConfigReducer, { initialInvoicesConfigState, InvoicesConfigState } from 'store/invoice-config/reducer';

interface ApplicationState {
    globalOverlay: GlobalOverlayState;
    session: SessionState;
    countries: CountriesState;
    currencies: CurrenciesState;
    declarations: DeclarationsState;
    breadcrumb: BreadcrumbItem[];
    customers: CustomersState;
    individuals: IndividualsState;
    notification: NotificationProps;
    drivers: DriversState;
    jobs: JobsState;
    codelists: CodelistsState;
    businessRules: BusinessRulesState;
    declarationFooter: DeclarationFooterState;
    products: ProductsState;
    documents: DocumentsState;
    taricCodes: TaricCodeState;
    digitalCertificates: DigitalCertificateState;
    declarationProduct: DeclarationProductState;
    exchanges: ExchangesState;
    commodityCodeTaxes: CommodityCodeTaxesState;
    tooltips: TooltipsState;
    declarationValidation: DeclarationValidation;
    cargos: CargosState;
    journeys: JourneysState;
    legs: LegsState;
    routes: RoutesState;
    vehicles: VehiclesState;
    declarationFormErrors: ParsedForm;
    refunds: RefundsState;
    depositRefunds: RefundsDepositState;
    weighbridges: WeighbridgesState;
    productTemplateFormErrors: ProductTemplateFormErrors;
    declarationFilter: DeclarationFilter;
    gvmsReferenceData: GvmsReferenceDataStore;
    manifests: ManifestsState;
    teams: TeamsState;
    invoices: InvoicesState;
    invoicesConfig: InvoicesConfigState;
}

const reducers: Reducer<ApplicationState> = combineReducers<ApplicationState>({
    session,
    declarations,
    countries,
    currencies,
    breadcrumb,
    customers,
    individuals,
    notification,
    drivers,
    globalOverlay,
    jobs,
    codelists,
    businessRules,
    declarationFooter,
    products,
    documents,
    taricCodes,
    digitalCertificates,
    declarationProduct,
    exchanges,
    commodityCodeTaxes,
    tooltips,
    declarationValidation,
    cargos,
    journeys,
    legs,
    routes,
    vehicles,
    declarationFormErrors,
    refunds,
    depositRefunds,
    weighbridges,
    productTemplateFormErrors,
    declarationFilter,
    gvmsReferenceData: gvmsReferenceDataReducer,
    manifests,
    teams,
    invoices: invoicesReducer,
    invoicesConfig: invoiceConfigReducer,
});

// Logging out is also how a tenant switch happens (no page reload), so every slice must forget
// the previous tenant's data. Passing undefined makes each reducer return its initial state.
export const rootReducer: Reducer<ApplicationState> = (state, action) => {
    const isLogout = action.type === 'USER_LOGOUT_SUCCESS';
    const stateToReduce = isLogout ? undefined : state;
    return reducers(stateToReduce, action);
};

export const store = configureStore();

export function configureStore(): Store<ApplicationState> {
    const middlewares = [loggerMiddleware, thunkMiddleware];
    const middlewareEnhancer = applyMiddleware(...middlewares);

    const composeEnhancers = composeWithDevTools({});

    // const enhancers = [middlewareEnhancer];

    // // If Redux DevTools Extension is installed use it, otherwise use Redux compose
    // const composeEnhancers =
    //   (window as any).__REDUX_DEVTOOLS_EXTENSION__ &&
    //   (window as any).__REDUX_DEVTOOLS_EXTENSION__();

    const composedEnhancers = composeEnhancers(middlewareEnhancer);

    const initialState: ApplicationState = {
        globalOverlay: globalOverlayInitialState,
        session: sessionInitialState,
        countries: countriesInitialState,
        currencies: currenciesInitialState,
        declarations: initialDeclarationsState,
        breadcrumb: [],
        customers: customersInitialState,
        individuals: individualsInitialState,
        notification: notificationInitialState,
        drivers: driversInitialState,
        jobs: jobsInitialState,
        codelists: codelistsInitialState,
        businessRules: businessRulesInitialState,
        declarationFooter: declarationFooterInitialState,
        products: productsInitialState,
        documents: initalDocumentsState,
        taricCodes: taricCodeInitialState,
        digitalCertificates: initialDigitalCertificateState,
        declarationProduct: initialDeclarationProductState,
        exchanges: exchangesInitialState,
        commodityCodeTaxes: commodityCodeTaxesInitialState,
        tooltips: tooltipsInitialState,
        declarationValidation: decalarationValidationInitialState,
        cargos: cargosInitialState,
        journeys: journeysInitialState,
        legs: legsInitialState,
        routes: routesInitialState,
        vehicles: vehiclesInitialState,
        declarationFormErrors: declarationFormErrorsInitState,
        refunds: initialRefundsState,
        depositRefunds: initialRefundsDepositState,
        weighbridges: weighbridgesInitialState,
        productTemplateFormErrors: productTemplateFormErrorsInitState,
        declarationFilter: declarationFilterInitState,
        gvmsReferenceData: gvmsReferenceDataInitialState,
        manifests: manifestsInitialState,
        teams: initialTeamsState,
        invoices: invoicesInitialState,
        invoicesConfig: initialInvoicesConfigState,
    };

    const store = createStore(rootReducer, initialState, composedEnhancers);
    return store;
}

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
